import {
  PRIVACY_NOTICE_DATE,
  PRIVACY_NOTICE_VERSION,
  WAITLIST_NOTICE_DATE,
  WAITLIST_NOTICE_VERSION,
} from '../config/legalDocuments';
import type { LegalAddendum, LegalDocument } from './types';

/**
 * Melio Privacy Notice — Patient Facing.
 *
 * Ported VERBATIM from the controlled document "Melio_Privacy_Notice_v1.4.md"
 * (Melio Health Solutions Ltd, 11 September 2026). Do not paraphrase, tidy or
 * add to the wording; if this file and the controlled document disagree, the
 * document wins and this file is wrong. Update PRIVACY_NOTICE_VERSION /
 * PRIVACY_NOTICE_DATE in config/legalDocuments alongside any change, and
 * consider whether ONBOARDING_CONSENT_VERSION (types/consent) needs bumping.
 *
 * Sections are numbered 1–15 to match the document, which cross-references
 * itself by number (sections 3, 6 and 7). Contact addresses are the document's
 * own: help@meliotherapeutics.uk.
 */
export const PRIVACY_DOCUMENT: LegalDocument = {
  id: 'privacy',
  kicker: 'Privacy',
  title: 'Privacy Notice',
  pageTitle: 'Privacy notice · Melio',
  description: 'How Melio Health Solutions Ltd uses personal information across its website, apps and service, how long it is kept, who receives it and your rights.',
  version: PRIVACY_NOTICE_VERSION,
  updated: PRIVACY_NOTICE_DATE,
  intro: [
    {
      type: 'p',
      text: 'Melio Health Solutions Ltd provides a doctor-led preventive health service. This notice explains how we use personal information across our website, apps and service, including registration, testing, clinician review, care planning and communications. It also explains how long we keep information, who receives it and your rights.',
    },
    {
      type: 'p',
      text: 'Read it with our [Terms of Service](/terms) and testing consent materials at [meliotherapeutics.uk](https://meliotherapeutics.uk).',
    },
  ],
  sections: [
    {
      number: 1,
      title: 'Who we are',
      blocks: [
        {
          type: 'p',
          text: 'Melio Health Solutions Ltd is the controller of the personal information described in this notice. That means we are responsible for deciding how and why it is used.',
        },
        {
          type: 'table',
          columns: ['', ''],
          rows: [
            ['**Company**', 'Melio Health Solutions Ltd, registered in England and Wales, company number 16497996'],
            ['**Registered office**', '167–169 Great Portland Street, 5th Floor, London W1W 5PF'],
            ['**ICO registration**', 'ZC153092'],
            ['**Privacy enquiries**', 'help@meliotherapeutics.uk — please mark your message "Privacy"'],
          ],
        },
      ],
    },
    {
      number: 2,
      title: 'Information we collect',
      blocks: [
        {
          type: 'p',
          text: '**Identity and contact.** Your name, date of birth, sex at birth and, where relevant to your care, gender; your email address, telephone number, postal address and account identifiers.',
        },
        {
          type: 'p',
          text: '**Health.** Your medical history, symptoms, medications, allergies, testing history, prior results, clinician notes, approval records and care plan.',
        },
        {
          type: 'p',
          text: '**Testing.** Raw genetic results, interpreted findings, biomarker values and their units and dates, and test-report references.',
        },
        {
          type: 'p',
          text: '**Family history.** Conditions in your family, the age at which a relative developed a condition where known, confirmed inherited conditions and the relevant side of the family.',
        },
        {
          type: 'p',
          text: '**Technical.** Device and browser information, sign-in and activity records, and security and audit logs.',
        },
        {
          type: 'p',
          text: '**Communications.** Platform messages, appointments, support requests, complaints and consultation records.',
        },
        {
          type: 'p',
          text: "**AI-assisted drafting.** Information supplied to the tools described in section 7, their drafts and the clinician's review record.",
        },
        { type: 'h3', text: 'Where we get it' },
        {
          type: 'p',
          text: 'Most information comes from you. We also receive results from laboratories, notes from clinicians, technical information from your use of the platform, and operational information from our scheduling, hosting, messaging and security providers.',
        },
      ],
    },
    {
      number: 3,
      title: 'Health and genetic information',
      blocks: [
        {
          type: 'p',
          text: 'UK law treats health and genetic information as special category data. We use it to provide health care under Article 9(2)(h) UK GDPR and paragraph 2 of Schedule 1 to the Data Protection Act 2018. It is handled by, or under the responsibility of, clinicians who owe duties of confidentiality.',
        },
        { type: 'p', text: 'We store structured blood-test values and report references rather than duplicate laboratory records.' },
        {
          type: 'p',
          text: 'We hold raw genetic results in a separate encrypted store. Access is restricted and logged. This allows later review as medical knowledge develops without requiring another sample. You can ask us to delete raw genetic data separately from the rest of your record.',
        },
      ],
    },
    {
      number: 4,
      title: 'Information about your relatives',
      blocks: [
        {
          type: 'p',
          text: 'We record limited information about relatives when assessing inherited risk. We use set fields where possible and do not ask for identifying details.',
        },
      ],
    },
    {
      number: 5,
      title: 'How we use your information, and our legal basis',
      blocks: [
        {
          type: 'table',
          columns: ['What we use it for', 'Lawful basis', 'Additional condition for health and genetic information'],
          rows: [
            ['Creating and managing your membership, and taking payment', 'Performance of our contract with you', '—'],
            ['Confirming your identity', 'Our legal obligations; our legitimate interest in preventing fraud', '—'],
            [
              'Assessing whether the service is suitable for you, collecting your health and family history, arranging your tests, receiving results, supporting clinician review, preparing your care plan, and contacting you about your care',
              'Performance of our contract with you',
              'Article 9(2)(h) — provision of health care',
            ],
            ['Patient safety, complaints, incidents and clinical audit', 'Our legal obligations; our legitimate interest in running a safe service', 'Article 9(2)(h)'],
            ['Running, monitoring and securing the platform', 'Our legitimate interest in a secure and reliable service', '—'],
            [
              'Improving the service, quality checks and internal reporting',
              'Our legitimate interests',
              'Article 9(2)(h), or information anonymised so that it no longer identifies you',
            ],
            ['Anything you specifically opt into', 'Your consent', 'Your explicit consent'],
            ['Establishing, exercising or defending legal claims', 'Our legal obligations; our legitimate interests', 'Article 9(2)(f)'],
          ],
        },
        {
          type: 'p',
          text: 'You may withdraw consent at any time. This does not affect earlier processing or processing based on another lawful basis.',
        },
        {
          type: 'p',
          text: 'Some information is necessary for us to provide the service at all. If you prefer not to give us your health or family history, we may not be able to offer you a clinical assessment or a care plan.',
        },
        {
          type: 'p',
          text: 'We use anonymised and aggregated information for analysis, service improvement and internal reporting. **We do not sell personal information.** We will not use identifiable information for external research or AI-model training without first telling you and obtaining explicit consent where required.',
        },
      ],
    },
    {
      number: 6,
      title: 'Our laboratories',
      blocks: [
        { type: 'p', text: 'Two laboratories carry out our tests. They provide analytical results; a Melio clinician interprets them for your care.' },
        {
          type: 'p',
          text: '**Blood tests.** Inuvi Diagnostics Limited tests samples in Churcham, Gloucester, and returns measured values, reference ranges and flags. It destroys physical samples 7 days after receipt. Inuvi Health Limited arranges home, clinic or kit-based collection.',
        },
        {
          type: 'p',
          text: '**Genetic tests.** AttoDiagnostics Ltd tests saliva or cheek-swab samples in Norfolk against a defined list of around 70 markers; it does not sequence your whole genome. It destroys physical samples about one month after receipt and holds coded genotype data for up to 30 years.',
        },
        {
          type: 'p',
          text: 'Melio applies fixed, clinician-approved rules to each marker. A clinician reviews each finding before it enters your record or care plan.',
        },
        {
          type: 'p',
          text: 'The genetic test provides preventive background information, not a diagnosis of an inherited condition. A clinician may recommend confirmatory testing, including through NHS Clinical Genetics, before action is taken.',
        },
      ],
    },
    {
      number: 7,
      title: 'Artificial intelligence',
      blocks: [
        {
          type: 'p',
          text: 'AI tools may help draft summaries, organise information and flag gaps. A clinician reviews care-related output before it is finalised, and we record that review. Software does not make care decisions alone.',
        },
        {
          type: 'p',
          text: '**AI does not interpret genetic results.** The conclusion comes from the rules described in section 6. AI may assist with wording only.',
        },
        {
          type: 'p',
          text: 'Our contracts prohibit AI providers from using your information to train their systems and require minimum retention. We limit the information sent and remove direct identifiers where possible.',
        },
      ],
    },
    {
      number: 8,
      title: 'Who we share information with',
      blocks: [
        { type: 'p', text: 'We share personal information with:' },
        {
          type: 'ul',
          items: [
            'the clinicians we engage to deliver your care',
            'our testing laboratories and sample-collection partners',
            'our hosting, cloud, authentication, messaging and infrastructure providers',
            'our scheduling, support and workflow providers',
            'our payment providers',
            'our professional advisers, insurers and auditors',
            'regulators, law enforcement and public authorities, where the law requires it or where it is necessary to protect you',
          ],
        },
        {
          type: 'p',
          text: 'We limit sharing to what each purpose requires. Processors act under written data-protection and confidentiality terms and may use information only on our instructions.',
        },
      ],
    },
    {
      number: 9,
      title: 'Information held outside the UK',
      blocks: [
        {
          type: 'p',
          text: 'Some providers process information outside the UK. We use UK adequacy regulations, the International Data Transfer Agreement or the UK Addendum to the EU Standard Contractual Clauses, as applicable. We also assess destination-country risk and apply technical access and storage controls.',
        },
      ],
    },
    {
      number: 10,
      title: 'How long we keep your information',
      blocks: [
        {
          type: 'ul',
          items: [
            '**Core clinical records** — your health intake, test results, clinician notes, care plan and care communications — for a minimum of **8 years** from your last clinical event, applying NHS records-management retention practice as a private-sector floor, and then reviewed.',
            '**Raw genetic data** — in the separate encrypted store described in section 3, and deletable on request independently of the rest of your record.',
            '**Incomplete registrations** — for a short period, and then deleted.',
            '**Backups** — held separately from live records, for a limited period.',
          ],
        },
        {
          type: 'p',
          text: 'Laboratory retention periods are set out in section 6. We will provide an extract from our full retention schedule on request.',
        },
      ],
    },
    {
      number: 11,
      title: 'Keeping your information secure',
      blocks: [
        {
          type: 'p',
          text: 'We use authenticated sign-in, role-based access, encryption, audit logs and supplier controls. Clinical access is limited to staff and clinicians who need it. Access to identifiable genetic data is restricted and logged.',
        },
      ],
    },
    {
      number: 12,
      title: 'Automated decisions',
      blocks: [
        {
          type: 'p',
          text: 'Software may draft, prompt or prioritise work, but a clinician decides testing, escalation and monitoring.',
        },
      ],
    },
    {
      number: 13,
      title: 'Your rights',
      blocks: [
        { type: 'p', text: 'You have the right to:' },
        {
          type: 'ul',
          items: [
            'obtain a copy of the information we hold about you',
            'have inaccurate information corrected',
            'have your information deleted, in certain circumstances',
            'restrict how we use your information',
            'receive your information in a portable form, or have it sent to another provider',
            'object to processing we carry out on the basis of our legitimate interests',
            'withdraw your consent, where we rely on it',
          ],
        },
        {
          type: 'p',
          text: 'Contact **help@meliotherapeutics.uk** to exercise a right. We may confirm your identity. We will respond within one month or explain why a complex request requires longer.',
        },
        {
          type: 'p',
          text: 'We may have to retain clinical information for safety, regulatory or medico-legal reasons. If so, we will explain what we kept and why.',
        },
      ],
    },
    {
      number: 14,
      title: 'Complaints',
      blocks: [
        { type: 'p', text: 'To complain about our use of information, contact **help@meliotherapeutics.uk**.' },
        { type: 'p', text: "You can also complain to the Information Commissioner's Office:" },
        {
          type: 'ul',
          items: [
            '[ico.org.uk](https://ico.org.uk)',
            '0303 123 1113',
            "Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF",
          ],
        },
      ],
    },
    {
      number: 15,
      title: 'Changes to this notice',
      blocks: [{ type: 'p', text: 'We may update this notice. We will tell you directly about significant changes.' }],
    },
  ],
  history: [
    {
      version: '1.4',
      date: '11 September 2026',
      change: 'Tightened public-facing copy; removed promotional and defensive phrasing while preserving the Article 13/14 disclosures.',
    },
    {
      version: '1.3',
      date: '7 September 2026',
      change:
        'Rewritten for register and length. Named individuals and internal reviewer names removed; DPO section removed; accreditation claim removed (and with it the scope qualifier it required); legal bases mapped to purposes in a table; transfer safeguards named; sections consolidated 18 to 15.',
    },
    {
      version: '1.2',
      date: '22 June 2026',
      change: 'Regulatory review (REP-001) — "testing history and prior tests"; UKAS accreditation scope qualifier.',
    },
    { version: '1.1', date: '10 June 2026', change: 'Prior issue for review.' },
  ],
};

/**
 * The website waitlist notice (W-8). NOT part of the controlled document:
 * the pre-launch waitlist predates it, so the Article 13 information for the
 * list is published as a separately-versioned addendum (see
 * WAITLIST_NOTICE_VERSION in config/legalDocuments). The waitlist form links to
 * /privacy#waitlist. Fold it into the notice at the document's next revision.
 */
export const PRIVACY_WAITLIST_ADDENDUM: LegalAddendum = {
  id: 'waitlist',
  kicker: 'Addendum',
  title: 'The website waitlist',
  version: WAITLIST_NOTICE_VERSION,
  date: WAITLIST_NOTICE_DATE,
  blocks: [
    {
      type: 'p',
      text: 'Melio has not opened to new members. Until it does, our website offers a waitlist so that people who would like to hear from us can ask to be told first. This addendum explains what we do with what you give us there. It supplements the notice above and does not change it; it is published separately because the notice above predates the waitlist, and it will be folded into that notice at its next revision.',
    },
    { type: 'h3', text: 'What we collect' },
    {
      type: 'ul',
      items: [
        'your first name and email address',
        'if you choose to answer them, four optional questions: the first half of your postcode, how soon you were thinking of starting, whether you might join alongside a partner or family member, and which of our areas of focus — sleep, movement, nutrition, bloodwork, metabolic health, genetic and inherited risk — interest you most. That last question asks which parts of our service you would like to hear about. It does not ask about your health, and ticking an area tells us what to staff for rather than anything about you',
        'which assessment you were reading about, if you arrived from a link on that page — this is taken from the link, not asked',
        'if you tell us, the name or code of whoever told you about Melio — so that we can thank the person who made the introduction',
        'how you reached us — for example the campaign or page you came from — so that we know which of our efforts actually reach people',
        'if you reserve a place, the payment reference, amount and date that Stripe gives us — never your card details, which we do not see or store',
      ],
    },
    { type: 'h3', text: 'If you name the person who told you about us' },
    {
      type: 'p',
      text: 'The box asking who told you about Melio is optional and you can leave it empty. If you fill it in, we match what you write against a list of our own people and partners so that we can recognise the person who made the introduction; a name we cannot match is looked at by a member of our team and then either matched or left as it is. We keep what you wrote alongside your entry. We do not tell the person you name that you have joined, and we do not use the answer for anything other than recognising introductions.',
    },
    { type: 'h3', text: 'What we deliberately do not collect' },
    {
      type: 'p',
      text: 'Nothing about your health. No symptoms, conditions, medications, family history, or health goals are collected on the waitlist, and the form does not ask for them. That means nothing you give us here is special-category data. We ask only for the first half of your postcode — never the full postcode — because we use it to decide which areas to open in, not to identify your address. We do not store your IP address.',
    },
    { type: 'h3', text: 'If you reserve a place' },
    {
      type: 'p',
      text: 'Reserving a place on the list costs a £50 deposit. The payment is taken by Stripe, our payment provider, on its secure checkout page; Stripe processes your card details under its own privacy notice and we never see or store them. We keep the payment reference, the amount and the date against your waitlist entry so that we can credit the deposit to your membership or refund it. Our legal basis for that is the contract you enter into when you pay.',
    },
    { type: 'h3', text: 'Why we use it, and our legal basis' },
    {
      type: 'p',
      text: 'We use it to email you when we open to new members, and to understand where demand is and what people are looking for so we can plan. Our legal basis is your **consent**, which you give by ticking the box on the form. You are free not to give it, and you can withdraw it at any time — every email we send has a one-click unsubscribe, and you can also write to us at the address below. Withdrawing does not affect anything we did before you withdrew.',
    },
    { type: 'h3', text: 'How long we keep it' },
    {
      type: 'p',
      text: 'Until we open and you have been offered a place, or until you ask us to remove you — and in any event no longer than 24 months after we last heard from you, at which point the entry is deleted. This is a shorter period than the eight years described above for member records, because a waitlist entry is not a clinical record and there is no reason to keep it once it has served its purpose. If we never open, we delete the list.',
    },
    { type: 'h3', text: 'Who sees it' },
    {
      type: 'p',
      text: "Only Melio staff who need it. Waitlist entries are not readable by other people on the list, they are not published, and we do not sell or share them for anyone else's marketing. The infrastructure and security described in the notice above applies to them too.",
    },
    { type: 'h3', text: 'Your rights' },
    {
      type: 'p',
      text: "The rights set out above apply here in full: you can ask us for a copy of what we hold about you, ask us to correct it, or ask us to delete it, and we will act on that request. Write to help@meliotherapeutics.uk. If you are unhappy with how we have handled your data you can complain to the Information Commissioner's Office at [ico.org.uk](https://ico.org.uk).",
    },
  ],
};
